/**
 * Regras puras do painel "Configurar pipeline".
 *
 * Espelham `crm.services.salvar_pipeline` para a tela recusar antes de mandar
 * e, principalmente, para nunca deixar o gestor num beco: excluir a coluna de
 * entrada sem escolher outra dava um erro com nome de campo
 * (`is_default_entrada`) e a unica saida era cancelar (CRM-37).
 */

import { TIPO_ETAPA_CONVERSAO } from "../../utils/crmPermissoes";

export const TIPO_CONVERSAO = TIPO_ETAPA_CONVERSAO;

// Mesmos textos do backend (salvar_pipeline): a tela e o servidor falam igual.
export const MSG_ENTRADA_AUSENTE = "Escolha qual coluna recebe os leads novos.";
export const MSG_ENTRADA_DUPLICADA =
  "Só uma coluna pode receber os leads novos — deixe marcada apenas uma.";
export const MSG_ENTRADA_CONVERSAO =
  "A coluna de conversão não pode receber os leads novos: lead que nasce nela " +
  "não vira ganho. Escolha outra coluna.";

// crm.services.LIMITE_DIAS_REGRA_PIPELINE e _ROTULOS_REGRAS_DE_DIAS.
export const LIMITE_DIAS_REGRA_PIPELINE = 3650;
export const ROTULO_AVISO_PARADOS = "Aviso de leads parados";
export const ROTULO_ARQUIVAR_PERDIDOS = "Arquivar leads perdidos";

const INTEIRO = /^[+-]?\d+$/;

export function ehColunaDeConversao(etapa) {
  return etapa?.tipo === TIPO_CONVERSAO;
}

function ativa(etapa) {
  return etapa.ativo !== false;
}

/** Mensagem do problema com a coluna de entrada, ou null quando esta certa. */
export function validarEntrada(etapas) {
  const entradas = (etapas || []).filter((e) => ativa(e) && e.is_default_entrada);
  if (entradas.length === 0) return MSG_ENTRADA_AUSENTE;
  if (entradas.length > 1) return MSG_ENTRADA_DUPLICADA;
  if (ehColunaDeConversao(entradas[0])) return MSG_ENTRADA_CONVERSAO;
  return null;
}

/** Lista nova com `key` como a UNICA entrada. */
export function marcarComoEntrada(etapas, key) {
  return etapas.map((e) => ({ ...e, is_default_entrada: e._key === key }));
}

/**
 * Colunas que podem virar a entrada: ativas e fora a de conversao. Colunas
 * novas (ainda sem id) valem — o servidor le a entrada do proprio payload.
 */
export function candidatasEntrada(etapas, excetoKey) {
  return (etapas || []).filter(
    (e) => e._key !== excetoKey && ativa(e) && !ehColunaDeConversao(e)
  );
}

/**
 * Para onde levar os leads de uma coluna excluida: so colunas ja salvas (o
 * remapeamento aponta por id) e nunca a de conversao — o remapeamento e
 * silencioso, e lead aberto parado em "Convertido" nao vira ganho.
 */
export function destinosDeRemapeamento(etapas, excetoKey) {
  return (etapas || []).filter(
    (e) => e._key !== excetoKey && e.id != null && ativa(e) && !ehColunaDeConversao(e)
  );
}

/**
 * Excluir pede confirmacao quando ha leads a mover, quando e a entrada ou
 * quando algum formulario do site manda leads para a coluna — nesse ultimo
 * caso a coluna podia sair sem nenhum aviso, e a captacao seguia apontando
 * para o vazio (FORM-01).
 */
export function exigeConfirmacaoRemocao(etapa, totalFormularios = 0) {
  if (etapa.is_default_entrada) return true;
  if ((Number(totalFormularios) || 0) > 0) return true;
  return etapa.id != null && (etapa.leads_count ?? 0) > 0;
}

/** Tira a coluna e, se informada, passa a entrada para `novaEntradaKey`. */
export function removerEtapa(etapas, key, novaEntradaKey) {
  const restantes = etapas.filter((e) => e._key !== key);
  return novaEntradaKey ? marcarComoEntrada(restantes, novaEntradaKey) : restantes;
}

/**
 * `{total, formularios:[nome]}` dos formularios do site que mandam leads para
 * a coluna. `porEtapa` e o `etapas` de `GET /api/formularios/etapas-em-uso`.
 */
export function formulariosDaEtapa(porEtapa, etapa) {
  const info = etapa?.id != null ? (porEtapa || {})[String(etapa.id)] : null;
  return {
    total: Number(info?.total) || 0,
    formularios: Array.isArray(info?.formularios) ? info.formularios : [],
  };
}

/** "3 formulários" para o resumo da linha; "" quando nenhum usa a coluna. */
export function rotuloFormulariosDaEtapa(info) {
  const total = info?.total || 0;
  if (total === 0) return "";
  return `${total} formulário${total === 1 ? "" : "s"}`;
}

/**
 * Aviso de quem depende da coluna que vai sair, e para onde esses formularios
 * passam a mandar os leads; "" quando nenhum formulario a usa.
 *
 * Sem ele, remover a coluna remapeava os formularios em silencio — e antes da
 * correcao do servidor a captacao do site simplesmente parava (FORM-01).
 */
export function avisoFormulariosDaColuna(info, destinoNome) {
  const total = info?.total || 0;
  if (total === 0) return "";
  const nomes = (info.formularios || []).map((n) => `“${n}”`);
  const restantes = total - nomes.length;
  const lista = nomes.length > 0
    ? ` (${nomes.join(", ")}${restantes > 0 ? ` e mais ${restantes}` : ""})`
    : "";
  const quantos = total === 1
    ? "1 formulário do site manda"
    : `${total} formulários do site mandam`;
  const destino = destinoNome ? `“${destinoNome}”` : "a coluna de entrada";
  return `${quantos} leads para esta coluna${lista}. Ao excluir, eles passam a mandar para ${destino}.`;
}

/**
 * Dias de uma regra do pipeline -> `{dias}` ou `{erro}`. Espelha
 * `crm.services._dias_da_regra`: inteiro (numero ou texto) de 0 a 3650,
 * 0 desliga a regra.
 */
export function validarDiasRegra(valor, rotulo) {
  const texto = valor === null || valor === undefined ? "" : String(valor).trim();
  if (!INTEIRO.test(texto)) {
    return { erro: `${rotulo}: informe um número inteiro de dias (0 desliga).` };
  }
  const dias = Number.parseInt(texto, 10);
  if (dias < 0 || dias > LIMITE_DIAS_REGRA_PIPELINE) {
    return {
      erro: `${rotulo}: use de 0 a ${LIMITE_DIAS_REGRA_PIPELINE} dias (0 desliga).`,
    };
  }
  return { dias };
}
